import { Fragment } from "react";
import { Typography } from "@/components/custom/typography";
import { useTypedTranslations } from "@/i18n/typed";
import { Media, MediaProduct } from "@/services/graphql/generated";

type DisplayType = 'LCD' | 'LED';

interface MediaInfoListProps {
  data: MediaProduct;
}

const getResolution = (data: Media) => {
  const { resolutionWidth, resolutionHeight } = data;
  if (resolutionWidth && resolutionHeight) {
    return `${resolutionWidth}*${resolutionHeight}`;
  }
};

const formatDisplaySize = (data: Media) => {
  if (!data) return '--';
  const { displayType, mediaSize, pitch } = data;
  const resolution = getResolution(data);

  switch (displayType) {
    case 'LCD':
      return `LCD ${mediaSize} inch / ${resolution}`;
    case 'LED':
      return `LED ${resolution} / P ${pitch}`;
    default:
      return resolution;
  }
};

const isDisplayType = (value?: string | null): value is DisplayType =>
  value === 'LCD' || value === 'LED';

const countDisplayType = (medias: Media[] = []) =>
  medias.reduce(
    (acc, { displayType }) => {
      if (!isDisplayType(displayType)) return acc;
      acc[displayType] = (acc[displayType] || 0) + 1;
      return acc;
    },
    {} as Record<DisplayType, number>
  );

const formatTotalDetailDisplay = (
  medias: Media[] = [],
  tUnit: (key: string) => string
) => {
  const counts = countDisplayType(medias);

  const parts = [
    counts.LCD && `LCD:${counts.LCD}${tUnit('quantity.device')}`,
    counts.LED && `LED:${counts.LED}${tUnit('quantity.device')}`,
  ].filter(Boolean);

  return parts.length ? `(${parts.join(' / ')})` : '';
};

export function MediaInfoList({ data }: MediaInfoListProps) {
  const firstMediaGroup = data?.mediaGroups?.mediaGroups?.[0];
  const medias = firstMediaGroup?.medias?.medias ?? [];
  const firstMedia = medias[0];
  const trafficValue = medias[0]?.trafficValue ?? 0;

  const t = useTypedTranslations("mediaOnMap");
  const tUnit = useTypedTranslations("unit");

  const mediaResolution = formatDisplaySize(firstMedia);
  const totalDetailDisplay = formatTotalDetailDisplay(medias, tUnit);

  const mediaProductInfo = [
    {
      label: t("labels.media"),
      value: mediaResolution ?? "--",
    },
    {
      label: t("labels.totalUnits"),
      value: `${medias.length} ${t("units.unit")} ${totalDetailDisplay}`,
    },
    {
      label: t("labels.footTraffic"),
      value: trafficValue,
    },
  ];

  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
      {mediaProductInfo.map((item) => (
        <Fragment key={item.label}>
          <Typography
            variant="detail1"
            tone="description"
            className="whitespace-nowrap"
          >
            {item.label}
          </Typography>
          <Typography
            variant="detail1"
            tone="medium"
            className="font-medium min-w-0"
          >
            {item.value}
          </Typography>
        </Fragment>
      ))}
    </div>
  );
}
